class BurgerMenu extends HTMLElement {
	constructor() {
		super();
		this.header = document.querySelector(".header-wrapper");
		this.burgerMenu = this.querySelector(".burger-menu");
		this.openButton = this.querySelector(".burger-menu__toggle--open-btn");
		this.openhoverButton = this.querySelector(".burger-menu__hover--open-btn");
		this.closeButton = this.querySelectorAll(".burger-menu__toggle--close-btn");
		this.overlay = this.querySelector(".burger-menu__overlay");

		if (this.header) this.header.preventHide = false;

		this.setListeners();
	}

	open(event) {
		event.preventDefault();
		if (this.header) this.header.preventHide = true;

		// Cancel any pending close so it can't fire after we open
		clearTimeout(this._closeTimer);
		this._closeTimer = null;

		if (this.burgerMenu.classList.contains('burger-menu--open')) {
			this.close(event);
			return;
		}

		const otherMenus = document.querySelectorAll(".burger-menu--open");

		if(otherMenus?.length){
			otherMenus.forEach(otherMenu => {
				if (otherMenu !== this.burgerMenu) {
					otherMenu.classList.remove("burger-menu--open");
					otherMenu.querySelector(".burger-menu__overlay")?.classList?.remove("burger-menu__overlay--active");
					otherMenu.closest('burger-menu')?._stopWatchdog?.();

					const otherDropdownWrapper = otherMenu.querySelector('.burger-menu__dropdown-wrapper');
					otherDropdownWrapper.style.transform = this.getTranslatePosition();
					// Mark as a switch so isSwitching detection works even when close() wasn't called
					BurgerMenu._lastHoverClose = Date.now();
				}
			});
		}

		this.burgerMenu.classList.add("burger-menu--open");
		this.overlay.classList.add("burger-menu__overlay--active");
		document.body.classList.add("overflow-hidden-drawer");

		if (this.dataset.openOnHover === 'true') this._startWatchdog();

		// Skip fade-in only when switching directly between hover menus (mouseleave → mouseenter
		// within ~100ms). A genuine re-open after clicking outside takes longer, so it gets
		// the normal fade-in animation.
		const incomingWrapper = this.burgerMenu.querySelector('.burger-menu__dropdown-wrapper');
		const isSwitching = this.dataset.openOnHover === 'true' &&
			incomingWrapper?.classList.contains('burger-menu__on-hover') &&
			BurgerMenu._lastHoverClose &&
			(Date.now() - BurgerMenu._lastHoverClose) < 100;
		if (isSwitching) incomingWrapper.style.transition = 'none';

		this.setDropdownWrapperPosition(this);

		if (isSwitching) {
			requestAnimationFrame(() => requestAnimationFrame(() => {
				incomingWrapper.style.transition = '';
			}));
		}
	}

	close(event) {
		event.preventDefault();
		this.burgerMenu.classList.remove("burger-menu--open");
		this.overlay.classList.remove("burger-menu__overlay--active");
		document.body.classList.remove("overflow-hidden-drawer");
		this._stopWatchdog();

		if (this.header) this.header.preventHide = false;

		this.burgerMenu.classList.add("hiding");

		if (this.dataset.openOnHover === 'true') {
			this.burgerMenu.classList.remove("hiding");
			BurgerMenu._lastHoverClose = Date.now();
		} else {
			setTimeout(() => this.burgerMenu.classList.remove("hiding"), 700);
		}

		this.clearDropdownWrapperPosition();
	}

	setListeners() {
		if (this.dataset.openOnHover === 'true') {
			const dropdownWrapper = this.burgerMenu?.querySelector('.burger-menu__dropdown-wrapper');

			// Primary close: leaving the panel closes right away (unless heading straight back
			// to the trigger). This is the behavior people actually expect from a hover menu.
			this.openhoverButton?.addEventListener("mouseenter", (event) => {
				clearTimeout(this._closeTimer);
				this._closeTimer = null;
				if (this.burgerMenu.classList.contains('burger-menu--open')) return;
				this.open(event);
			});
			dropdownWrapper?.addEventListener('mouseenter', () => {
				clearTimeout(this._closeTimer);
				this._closeTimer = null;
			});
			dropdownWrapper?.addEventListener('mouseleave', (event) => {
				const movedTo = event.relatedTarget;
				// Stay open if cursor moves back to the nav trigger
				if (movedTo && this.openhoverButton?.contains(movedTo)) return;

				// Only close on the way out the bottom/sides. Exiting back out the top (heading
				// toward the header) is usually just the cursor passing back over nav items on
				// its way elsewhere, not an intent to leave the menu — closing on that caused a
				// visible flash (instant close, then reopen a moment later).
				const rect = dropdownWrapper.getBoundingClientRect();
				if (event.clientY <= rect.top) return;

				this.close(event);
			});

			this._dropdownWrapper = dropdownWrapper;

			if (!BurgerMenu._trackingMouse) {
				BurgerMenu._trackingMouse = true;
				BurgerMenu._mouse = {};
				document.addEventListener('mousemove', (e) => {
					BurgerMenu._mouse = { x: e.clientX, y: e.clientY };
				}, { passive: true });
			}
		}

		this.openButton?.addEventListener("click", (event) => this.open(event));
		this.closeButton?.forEach((item) =>
			item.addEventListener("click", (event) => this.close(event))
		);
		this.overlay?.addEventListener("click", (event) => this.close(event));
		this.burgerMenu?.addEventListener(
			"keyup",
			(event) => event.code === "Escape" && this.close(event)
		);
	}

	// Safety net for hover menus: the trigger and the panel don't sit flush against each
	// other (a real gap can open up between them from containing-block quirks, sibling header
	// elements, etc.), so crossing from one to the other can miss mouseleave/enter on both —
	// nothing in setListeners() ever runs, and the menu is stuck open. Rather than trying to
	// plug that gap with more DOM listeners, poll the cursor's actual position against both
	// elements' real rects; if it's been over neither for a few seconds, force the menu closed
	// regardless of what events did or didn't fire.
	_startWatchdog() {
		const ABANDON_MS = 3000;
		this._lastSeenInsideAt = Date.now();
		clearInterval(this._watchdog);
		this._watchdog = setInterval(() => {
			const { x, y } = BurgerMenu._mouse || {};
			const inRect = (rect) => rect && x != null &&
				x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
			const triggerRect = this.openhoverButton?.getBoundingClientRect();
			const panelRect = this._dropdownWrapper?.getBoundingClientRect();
			if (inRect(triggerRect) || inRect(panelRect)) {
				this._lastSeenInsideAt = Date.now();
			} else if (Date.now() - this._lastSeenInsideAt > ABANDON_MS) {
				this.close({ preventDefault() {} });
			}
		}, 400);
	}

	_stopWatchdog() {
		clearInterval(this._watchdog);
		this._watchdog = null;
	}

	setDropdownWrapperPosition(el) {
		const dropdownWrapper = el.querySelector('.burger-menu__dropdown-wrapper');
		if (!dropdownWrapper) return;

		const header = document.querySelector(".shopify-section-header");
		const burgerMenu = el.querySelector(".burger-menu");
		const burgerMenuOverlay = el.querySelector(".burger-menu__overlay");

		// Land the panel's top edge exactly on the header's real on-screen bottom rather than
		// assuming "top: header height" lines up with the viewport — a will-change/transform
		// ancestor can make burgerMenu's containing block something other than the viewport,
		// which silently offsets that assumption and leaves an uncovered strip between the nav
		// trigger and the panel (confirmed live: cursor lands in that strip on a header sibling
		// like .menu_left_block instead of the panel, and the hover menu closes underneath it).
		// Self-calibrate against burgerMenu's own current rendered position instead.
		const targetViewportTop = header && this.isElementInViewport(header) ? header.getBoundingClientRect().bottom : 0;
		const renderedTop = burgerMenu.getBoundingClientRect().top;
		const currentTopStyle = parseFloat(getComputedStyle(burgerMenu).top) || 0;
		const calcTranslatePosition = `${currentTopStyle + (targetViewportTop - renderedTop)}px`;

		if (this.dataset.openOnHover === 'true') {
			dropdownWrapper.style.opacity = 1;
		}

		// Align to the visible text (.list-menu-line), not the padded .header__menu-item box —
		// otherwise the column header lands under the item's left padding instead of its label.
		const firstNavLink = document.querySelector('.list-menu--inline > li:first-child .list-menu-line, .list-menu--inline > li:first-child .header__menu-item, .list-menu--inline > li:first-child a');
		const navLeft = firstNavLink ? Math.round(firstNavLink.getBoundingClientRect().left) : 0;
		dropdownWrapper.style.setProperty('--menu-align-offset', `${navLeft}px`);

		dropdownWrapper.style.transform = `translateY(0)`;
		burgerMenu.style.top = calcTranslatePosition;
		burgerMenuOverlay.style.top = calcTranslatePosition;
	}

	clearDropdownWrapperPosition() {
		const dropdownWrappers = document.querySelectorAll('.burger-menu__dropdown-wrapper');
		dropdownWrappers?.forEach((el) => {
			el.style.transform = this.getTranslatePosition();

			if (el.classList.contains('burger-menu__on-hover')) {
				el.style.opacity = 0;
			}

			const burgerMenu = el.closest(".burger-menu");
			const burgerMenuOverlay = el.previousElementSibling;
			if(burgerMenu) {
				burgerMenu.style.top = 0;
			}
			if (burgerMenuOverlay) {
				burgerMenuOverlay.style.top = 0;
			}
		});
	}

	getTranslatePosition() {
		const header = document.querySelector(".shopify-section-header");
		const announcement = document.querySelector(".section-announcement");
		const countdownTimerBar = document.querySelector(".section-countdown-timer-bar");

		const calcTranslatePosition = this.getElementHeight(header) + this.getElementHeight(announcement) + this.getElementHeight(countdownTimerBar);
		return `translateY(calc(-100% - ${calcTranslatePosition}px))`;
	}

	getElementHeight(el) {
		if (!el) return 0;

		if (this.isElementInViewport(el)) {
			return el?.getBoundingClientRect()?.height || 0;
		}
		return 0;
	}

	isElementInViewport(el) {
		const rect = el.getBoundingClientRect();
		return (
			rect.top < window.innerHeight &&
			rect.bottom > 0 &&
			rect.left < window.innerWidth &&
			rect.right > 0
		);
	}
}

if (!customElements.get("burger-menu")) {
	customElements.define("burger-menu", BurgerMenu);
}
